/**
 * Notification Icons Mapping
 *
 * Maps notification types to Lucide icons and priority levels to color schemes.
 * Used by NotificationItem component to display appropriate icons.
 */

import {
  Bell,
  Package,
  ShoppingCart,
  CheckCircle,
  XCircle,
  MessageSquare,
  DollarSign,
  AlertCircle,
  Star,
  Shield,
  CheckSquare,
  Calendar,
  RefreshCw,
  AlertTriangle,
  User,
  Megaphone,
  UserCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { NotificationType, NotificationPriority } from '../types';

/**
 * Icon mapping for each notification type
 */
export const NOTIFICATION_ICONS: Record<NotificationType, LucideIcon> = {
  ORDER_STATUS_CHANGED: Package,
  NEW_PROPOSAL: ShoppingCart,
  PROPOSAL_ACCEPTED: CheckCircle,
  PROPOSAL_REJECTED: XCircle,
  MESSAGE_RECEIVED: MessageSquare,
  PAYMENT_RECEIVED: DollarSign,
  PAYMENT_FAILED: AlertCircle,
  REVIEW_RECEIVED: Star,
  VERIFICATION_REQUIRED: Shield,
  VERIFICATION_COMPLETED: CheckSquare,
  SUBSCRIPTION_EXPIRING: Calendar,
  SUBSCRIPTION_RENEWED: RefreshCw,
  SECURITY_ALERT: AlertTriangle,
  ACCOUNT_UPDATED: User,
  SYSTEM_ANNOUNCEMENT: Megaphone,
  WORKER_ASSIGNED: UserCheck,
  ORDER_COMPLETED: CheckCircle,
};

/**
 * Priority color schemes
 * Used for icon color, background, and border
 */
interface PriorityColors {
  icon: string; // Tailwind class for icon color
  bg: string; // Tailwind class for background
  border: string; // Tailwind class for border
}

export const PRIORITY_COLORS: Record<NotificationPriority, PriorityColors> = {
  HIGH: {
    icon: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  MEDIUM: {
    icon: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  LOW: {
    icon: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
};

/**
 * Get icon component for notification type
 *
 * @param type - Notification type
 * @returns Lucide icon component
 *
 * @example
 * const Icon = getNotificationIcon('NEW_PROPOSAL');
 * return <Icon className="h-5 w-5" />;
 */
export function getNotificationIcon(type: NotificationType): LucideIcon {
  return NOTIFICATION_ICONS[type] || Bell;
}

/**
 * Get color scheme for priority level
 *
 * @param priority - Notification priority
 * @returns Object with Tailwind class names for icon, bg, and border
 *
 * @example
 * const colors = getPriorityColors('HIGH');
 * return <div className={colors.bg}><Icon className={colors.icon} /></div>;
 */
export function getPriorityColors(priority: NotificationPriority): PriorityColors {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS.LOW;
}
